/**
 * The since-you-last-looked banner (TEMPORAL §3.1) — top strip on the
 * product page and the guide. Renders ONLY when code_changed + new + removed
 * > 0 vs the caller's baseline (the retold law: a guide upgrade that
 * re-narrates everything shows nothing here).
 *
 * "Got it" stamps the seen-snapshot and the banner drops. "See what changed"
 * opens the Codebase page's changes panel (deep link `#changes`) and stamps.
 * Merely rendering the banner never stamps — it must survive a
 * glance-and-leave.
 */

import { Link } from 'react-router';

import type { Ulid } from '../../api/types';
import { codebasePath } from '../../lib/deepLink';
import { plural } from '../../lib/format';
import { Button } from '../../lib/ui';
import { bannerCount, useMarkSeen, useRepoChanges } from './api';

export function ChangesBanner({
  productId,
  repoId,
  framed = false,
}: {
  productId: Ulid;
  repoId: Ulid;
  /** true = a bordered card (page body); false = the guide's full-width strip */
  framed?: boolean;
}) {
  const changes = useRepoChanges(repoId.length > 0 ? repoId : undefined);
  const markSeen = useMarkSeen(repoId);

  if (productId.length === 0 || repoId.length === 0) return null;
  if (changes.data === undefined || changes.data === null) return null;
  const data = changes.data;
  const changed = bannerCount(data);
  if (changed === 0) return null;

  return (
    <div
      className={`flex items-center justify-between gap-4 bg-surface px-4 py-2 ${
        framed ? 'mb-4 rounded-card border border-default' : 'border-b border-default'
      }`}
      data-testid="changes-banner"
    >
      <span className="text-secondary">
        <span className="font-medium text-primary">
          Since you last looked: {plural(changed, 'area')} of the code changed.
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {/* Opening does not stamp seen here: the panel stamps once it has
            shown the diff, and it holds the diff it opened with. Stamping on
            the click re-baselined the changes queries while the (lazy) page
            was still loading, so the panel could open on "Nothing changed"
            (CI, 2026-09-08). "Got it" below is the explicit dismissal. */}
        <Link
          to={`${codebasePath(productId, repoId)}#changes`}
          data-testid="changes-banner-open"
          className="inline-flex h-8 items-center rounded-card border border-strong bg-raised px-3 text-[length:var(--text-ui-size)] text-primary no-underline hover:bg-inset"
        >
          See what changed
        </Link>
        <Button
          kind="ghost"
          testId="changes-banner-dismiss"
          disabled={markSeen.isPending}
          onClick={() => markSeen.mutate(data.current.id)}
        >
          Got it
        </Button>
      </span>
    </div>
  );
}
